import type { SdkHooks } from '@Pages/pageRuntime/sdk/types';

type LazyHook<THook> = {
  hook: THook;
  isLoaded: () => boolean;
  preload: () => Promise<void>;
};

// Throw the import promise so PageRenderer's Suspense boundary can load optional hooks on demand.
function lazyHook<THook extends (...args: any[]) => any>(
  load: () => Promise<THook>
): LazyHook<THook> {
  let hook: THook | null = null;
  let loading: Promise<void> | null = null;
  const preload = () => {
    loading ??= load().then((loaded) => {
      hook = loaded;
    });

    return loading;
  };
  const facade = ((...args) => {
    if (!hook) {
      throw preload();
    }

    return hook(...args);
  }) as THook;

  return { hook: facade, isLoaded: () => hook !== null, preload };
}

const lazyHooks = {
  useCreateResourceRecordForm: lazyHook<SdkHooks['useCreateResourceRecordForm']>(() =>
    import('./useCreateResourceRecordForm').then((module) => module.useCreateResourceRecordForm)
  ),
  useUpdateResourceRecordForm: lazyHook<SdkHooks['useUpdateResourceRecordForm']>(() =>
    import('./useUpdateResourceRecordForm').then((module) => module.useUpdateResourceRecordForm)
  ),
  useWorkflowForm: lazyHook<SdkHooks['useWorkflowForm']>(() =>
    import('./useWorkflowForm').then((module) => module.useWorkflowForm)
  ),
  useWorkspaceFieldsForm: lazyHook<SdkHooks['useWorkspaceFieldsForm']>(() =>
    import('./useWorkspaceFieldsForm').then((module) => module.useWorkspaceFieldsForm)
  ),
  useDataTable: lazyHook<SdkHooks['useDataTable']>(() =>
    import('./useDataTable').then((module) => module.useDataTable)
  ),
  useResourceDataTable: lazyHook<SdkHooks['useResourceDataTable']>(() =>
    import('./useResourceDataTable').then((module) => module.useResourceDataTable)
  ),
  useCalendarView: lazyHook<SdkHooks['useCalendarView']>(() =>
    import('@Pages/pageRuntime/components/calendar/useCalendarView').then(
      (module) => module.useCalendarView
    )
  ),
  useCalendarEvents: lazyHook<SdkHooks['useCalendarEvents']>(() =>
    import('@Pages/pageRuntime/components/calendar/useCalendarEvents').then(
      (module) => module.useCalendarEvents
    )
  ),
  useNewCalendarEvent: lazyHook<SdkHooks['useNewCalendarEvent']>(() =>
    import('@Pages/pageRuntime/components/calendar/useNewCalendarEvent').then(
      (module) => module.useNewCalendarEvent
    )
  ),
  useCalendarSelection: lazyHook<SdkHooks['useCalendarSelection']>(() =>
    import('@Pages/pageRuntime/components/calendar/useCalendarSelection').then(
      (module) => module.useCalendarSelection
    )
  )
};

export const useCreateResourceRecordForm = lazyHooks.useCreateResourceRecordForm.hook;
export const useUpdateResourceRecordForm = lazyHooks.useUpdateResourceRecordForm.hook;
export const useWorkflowForm = lazyHooks.useWorkflowForm.hook;
export const useWorkspaceFieldsForm = lazyHooks.useWorkspaceFieldsForm.hook;
export const useDataTable = lazyHooks.useDataTable.hook;
export const useResourceDataTable = lazyHooks.useResourceDataTable.hook;
export const useCalendarView = lazyHooks.useCalendarView.hook;
export const useCalendarEvents = lazyHooks.useCalendarEvents.hook;
export const useNewCalendarEvent = lazyHooks.useNewCalendarEvent.hook;
export const useCalendarSelection = lazyHooks.useCalendarSelection.hook;

export function prepareLazyHooksForSource(
  sources: Array<string | null | undefined>
): Promise<void> | null {
  const loading: Promise<void>[] = [];

  for (const [name, loader] of Object.entries(lazyHooks)) {
    if (!loader.isLoaded() && sources.some((source) => source && source.includes(name))) {
      loading.push(loader.preload());
    }
  }

  return loading.length > 0 ? Promise.all(loading).then(() => undefined) : null;
}
